import React from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Brain,
  ExternalLink,
  FileSearch,
  Lock,
  MessageCircle,
  ShieldCheck,
  UserCheck,
  Users,
} from 'lucide-react';
import useInView from '../hooks/useInView';

const SHIELD = "/images/home/safety-research-hero.png";
const RONI = "/images/home/roni-feature.png";

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-3 font-outfit">{children}</p>
);

const SourceLink = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200 transition-colors"
  >
    {children}
    <ExternalLink size={12} />
  </a>
);

const researchStats = [
  {
    value: "33%",
    label: "of boys aged 9–12 reported an online sexual interaction in Thorn's 2024 youth research.",
    source: "Thorn Youth Perspectives",
    href: "https://www.thorn.org/blog/2024-youth-perspectives/",
  },
  {
    value: "30%",
    label: "of 9–12-year-olds reported an online sexual interaction with someone they believed to be an adult.",
    source: "Thorn Youth Perspectives",
    href: "https://www.thorn.org/blog/2024-youth-perspectives/",
  },
  {
    value: "1 in 3",
    label: "young people across 30 countries told UNICEF they had been a victim of online bullying.",
    source: "UNICEF U-Report poll",
    href: "https://www.unicef.org/press-releases/unicef-poll-more-third-young-people-30-countries-report-being-victim-online-bullying",
  },
  {
    value: "46%",
    label: "of U.S. teens aged 13–17 told Pew Research Center they had experienced at least one cyberbullying behavior.",
    source: "Pew Research Center",
    href: "https://www.pewresearch.org/internet/2022/12/15/teens-and-cyberbullying-2022/",
  },
];

const learnings = [
  {
    icon: Brain,
    title: "Harm often starts before a child understands what is happening.",
    body: "Online grooming can begin as attention, friendship, compliments, gaming help, emotional support, virtual gifts, or pressure to move into a more private space. That is why ROMIND KIDS is designed to detect early warning signs, not only obvious violations.",
    response: "RONI looks for repeated unsafe patterns such as requests for private photos, attempts to move conversations outside the platform, secrecy language, emotional pressure, location-seeking, or repeated contact with younger users.",
  },
  {
    icon: MessageCircle,
    title: "Children may not report harm immediately.",
    body: "A child may freeze, feel ashamed, fear punishment, or simply not understand that an interaction is unsafe. Thorn reported that one in five minors who experienced an online sexual interaction did not tell anyone about it.",
    response: "ROMIND KIDS does not rely only on manual reports. It combines child reporting tools, RONI warnings, automated safety signals, behavioral risk scoring, and parent or guardian escalation for serious cases.",
  },
  {
    icon: Users,
    title: "Cyberbullying is not “just drama.”",
    body: "Cyberbullying can affect mental health, school attendance, social confidence, and a child's sense of safety. UNICEF reported that one in five young people in its poll had skipped school due to cyberbullying and violence.",
    response: "ROMIND KIDS uses positive-first social design: no public dislike counters, no humiliation mechanics, no toxic popularity ranking, limited negative reactions, report/block tools, and RONI support inside communication spaces.",
  },
  {
    icon: ShieldCheck,
    title: "Safety must be built before scale.",
    body: "Google Play requires apps with user-generated content to provide ongoing moderation, define prohibited content and behavior, and include in-app reporting and blocking systems for users and content.",
    response: "ROMIND KIDS is built around prevention, not only reaction: content checks, message checks, report/block tools, parent controls, child safety standards, human review, account restriction modes, and escalation protocols.",
  },
];

const safetyLayers = [
  {
    icon: MessageCircle,
    title: "Real-time message safety checks",
    desc: "Messages, comments, captions, hashtags, and profile text can be reviewed for bullying, threats, manipulation, sexual solicitation, private information sharing, and unsafe external-contact attempts.",
  },
  {
    icon: FileSearch,
    title: "Photo and video safety review",
    desc: "Uploaded media can be scanned before publication. Suspicious content may be blocked, limited, hidden pending review, or escalated to human safety review.",
  },
  {
    icon: Brain,
    title: "Voice-message review",
    desc: "Voice messages can be checked for unsafe language, threats, harassment, coercion, sexualized content, aggressive pressure, and other high-risk communication signals.",
  },
  {
    icon: AlertTriangle,
    title: "Behavioral risk detection",
    desc: "The system is designed to look beyond one message and detect repeated risk patterns over time, including unusual friend requests, secrecy language, repeated photo requests, pressure to move outside the platform, or adult-like behavior from a child account.",
  },
  {
    icon: UserCheck,
    title: "Human Safety Review",
    desc: "When a high-risk pattern appears, a case can be escalated to trained safety reviewers. Reviewers should receive only the information necessary to evaluate the specific safety risk.",
  },
  {
    icon: Lock,
    title: "Parent/Guardian escalation",
    desc: "For serious safety cases, social features may be temporarily restricted until a parent or guardian is involved and the account receives additional safety verification.",
  },
];

const sources = [
  {
    name: "Thorn — Youth Perspectives on Online Safety, 2024",
    desc: "Youth data on online sexual interactions, grooming, sextortion, disclosure gaps, and younger boys being targeted across gaming, social platforms, and messaging spaces.",
    href: "https://www.thorn.org/blog/2024-youth-perspectives/",
  },
  {
    name: "UNICEF — U-Report poll on online bullying",
    desc: "Global youth polling across 30 countries on online bullying, school absence, and the need for shared responsibility among governments, platforms, communities, and young people.",
    href: "https://www.unicef.org/press-releases/unicef-poll-more-third-young-people-30-countries-report-being-victim-online-bullying",
  },
  {
    name: "Pew Research Center — Teens and Cyberbullying 2022",
    desc: "U.S. teen research on cyberbullying behaviors, online harassment, platform accountability, and teen views on what could reduce online abuse.",
    href: "https://www.pewresearch.org/internet/2022/12/15/teens-and-cyberbullying-2022/",
  },
  {
    name: "WeProtect Global Alliance — Global Threat Assessment 2025",
    desc: "Global analysis of technology-facilitated child sexual exploitation and abuse, with a prevention-focused framework for digital safety.",
    href: "https://www.weprotect.org/global-threat-assessment-25/",
  },
  {
    name: "Google Play — User Generated Content Policy",
    desc: "Platform requirements for UGC moderation, prohibited behavior, reporting systems, blocking tools, and action against objectionable content and users.",
    href: "https://support.google.com/googleplay/android-developer/answer/9876937?hl=en",
  },
  {
    name: "Google Play — Child Safety Standards Policy",
    desc: "Requirements for in-scope social apps to publish CSAE standards, provide an in-app feedback mechanism, address CSAM, comply with child safety laws, and provide a child safety point of contact.",
    href: "https://support.google.com/googleplay/android-developer/answer/14747720?hl=en",
  },
];

const SafetyResearch = () => (
  <main className="bg-[#05050A] min-h-screen">
    {/* Hero */}
    <section className="relative pt-32 pb-20 overflow-hidden">
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/15 rounded-full blur-[140px] pointer-events-none animate-nebula" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-500/12 rounded-full blur-[120px] pointer-events-none animate-nebula delay-300" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionLabel>Child Safety Research</SectionLabel>
            <h1 className="font-outfit font-black text-5xl sm:text-6xl text-white mb-6 tracking-tight leading-[1.05]">
              We studied the risks.<br />
              <span className="gradient-text">Then we designed differently.</span>
            </h1>
            <p className="text-white/60 text-base sm:text-lg leading-relaxed mb-6">
              ROMIND KIDS was designed around a simple principle: children should not have to face online risks alone. Research on grooming, cyberbullying, sextortion, harassment, and unsafe private messaging shaped our safety-first platform architecture from the beginning.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "Prevention-first safety architecture",
                "AI-assisted RONI Guardian Layer™",
                "Behavioral risk detection",
                "Human review for high-risk cases",
              ].map((item, i) => (
                <div key={i} className="rounded-xl bg-white/4 border border-white/8 p-3 flex items-center gap-2.5">
                  <ShieldCheck size={15} className="text-emerald-400 flex-shrink-0" />
                  <span className="text-white/60 text-xs font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-cyan-500/20 rounded-full blur-[90px]" />
              <img src={SHIELD} alt="ROMIND KIDS child safety shield" className="relative w-full max-w-xs animate-float-slow" />
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Research stats */}
    <section data-testid="safety-research-stats" className="py-16 bg-[#080714]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>What research shows</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl text-white mb-4 tracking-tight max-w-3xl">
            Online child safety is not a theoretical problem.
          </h2>
          <p className="text-white/50 text-sm leading-relaxed max-w-3xl mb-10">
            Child-safety organizations and youth surveys show that online risks can include sexual solicitation, grooming, sextortion, cyberbullying, harassment, manipulation, and pressure to hide harmful interactions from trusted adults.
          </p>
        </FadeIn>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {researchStats.map((stat, i) => (
            <FadeIn key={stat.value} delay={i * 70}>
              <div data-testid={`safety-research-stat-${i}`} className="glass-card p-5 h-full border border-purple-500/10">
                <p className="font-outfit font-black text-4xl text-white mb-3 gradient-text">{stat.value}</p>
                <p className="text-white/55 text-xs leading-relaxed mb-4">{stat.label}</p>
                <SourceLink href={stat.href}>{stat.source}</SourceLink>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    {/* Framing */}
    <section className="py-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <div className="glass-card p-7 sm:p-9 border border-cyan-500/15">
            <SectionLabel>Our design position</SectionLabel>
            <h2 className="font-outfit font-bold text-3xl text-white mb-4 tracking-tight">
              ROMIND KIDS is not built as an open anonymous social network.
            </h2>
            <p className="text-white/55 text-sm leading-relaxed mb-4">
              It is a child-focused creative platform with safety-by-design architecture, parent-controlled social features, AI-assisted moderation, behavioral risk detection, human safety review, and escalation protocols for high-risk cases.
            </p>
            <p className="text-white/45 text-xs leading-relaxed">
              ROMIND KIDS is in active development. This page describes our safety direction, research foundation, and intended platform safeguards. Formal certifications, app-store reviews, and independent assessments require separate review processes.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>

    {/* Learnings */}
    <section data-testid="safety-research-learnings" className="py-16 bg-[#080714]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>What we learned</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl text-white mb-10 tracking-tight max-w-3xl">
            Each safety feature answers a real risk pattern.
          </h2>
        </FadeIn>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {learnings.map((item, i) => (
            <FadeIn key={item.title} delay={i * 80}>
              <div data-testid={`safety-learning-${i}`} className="glass-card p-6 h-full">
                <div className="w-11 h-11 rounded-2xl bg-purple-500/12 border border-purple-500/25 flex items-center justify-center mb-4">
                  <item.icon size={19} className="text-purple-300" />
                </div>
                <h3 className="font-outfit font-bold text-white text-lg mb-3 leading-snug">{item.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed mb-4">{item.body}</p>
                <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/15 p-4">
                  <p className="text-emerald-200/90 text-xs font-semibold mb-1">How this shaped ROMIND KIDS</p>
                  <p className="text-white/55 text-xs leading-relaxed">{item.response}</p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    {/* RONI architecture */}
    <section data-testid="roni-guardian-layer" className="py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-10 items-start">
          <FadeIn className="lg:sticky lg:top-24">
            <SectionLabel>Safety Architecture</SectionLabel>
            <h2 className="font-outfit font-bold text-3xl text-white mb-4 tracking-tight">RONI Guardian Layer™</h2>
            <p className="text-white/55 text-sm leading-relaxed mb-6">
              RONI Guardian Layer™ is the safety system behind ROMIND KIDS. It is designed to help detect unsafe interactions, reduce harmful behavior, support children in real time, and escalate serious risks to trained human review when needed.
            </p>
            <div className="relative w-fit mx-auto lg:mx-0">
              <div className="absolute inset-0 bg-cyan-500/20 rounded-full blur-[70px]" />
              <img src={RONI} alt="RONI Guardian Layer safety companion" className="relative w-44 sm:w-56 animate-float" />
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {safetyLayers.map((layer, i) => (
              <FadeIn key={layer.title} delay={Math.min(i * 60, 300)}>
                <div data-testid={`safety-layer-${i}`} className="glass-card p-5 h-full hover:border-cyan-500/25 transition-all">
                  <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4">
                    <layer.icon size={17} className="text-cyan-300" />
                  </div>
                  <h3 className="font-outfit font-bold text-white text-[15px] mb-2">{layer.title}</h3>
                  <p className="text-white/50 text-xs leading-relaxed">{layer.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Deterrence block */}
    <section data-testid="not-anonymous-playground" className="py-16 bg-[#080714]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <div className="relative overflow-hidden rounded-3xl border border-red-400/20 bg-gradient-to-br from-red-950/30 via-[#090714] to-purple-950/30 p-7 sm:p-10">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-red-500/10 rounded-full blur-[90px]" />
            <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-purple-600/12 rounded-full blur-[100px]" />
            <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_0.35fr] gap-8 items-center">
              <div>
                <SectionLabel>Zero tolerance for unsafe behavior</SectionLabel>
                <h2 className="font-outfit font-black text-3xl sm:text-4xl text-white mb-4 tracking-tight">
                  ROMIND KIDS is not an anonymous playground.
                </h2>
                <p className="text-white/65 text-sm leading-relaxed mb-4">
                  Accounts that show repeated high-risk behavior may be restricted, reviewed by trained safety specialists, and escalated for parent or guardian verification. Attempts to manipulate, pressure, groom, threaten, exploit, harass, or endanger children are not tolerated.
                </p>
                <p className="text-white/50 text-sm leading-relaxed">
                  Our safety systems are designed to detect not only harmful content, but harmful behavior patterns. ROMIND KIDS is built for children, families, and safe creativity — not for anonymous abuse.
                </p>
              </div>
              <div className="flex justify-center lg:justify-end">
                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-red-500/10 border border-red-400/25 flex items-center justify-center shadow-[0_0_60px_rgba(248,113,113,0.15)]">
                  <Lock size={44} className="text-red-200" />
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>

    {/* Sources */}
    <section data-testid="sources-we-follow" className="py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>Sources we follow</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl text-white mb-4 tracking-tight max-w-3xl">
            Our safety direction is grounded in public research and platform policy.
          </h2>
          <p className="text-white/50 text-sm leading-relaxed max-w-3xl mb-10">
            These sources help inform ROMIND KIDS safety design, product priorities, platform rules, and future compliance preparation.
          </p>
        </FadeIn>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sources.map((source, i) => (
            <FadeIn key={source.name} delay={Math.min(i * 50, 300)}>
              <a
                href={source.href}
                target="_blank"
                rel="noreferrer"
                data-testid={`source-card-${i}`}
                className="glass-card p-5 h-full flex flex-col transition-all hover:border-cyan-500/25 group"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <h3 className="font-outfit font-bold text-white text-[15px] leading-snug group-hover:text-cyan-100 transition-colors">{source.name}</h3>
                  <ExternalLink size={15} className="text-white/30 group-hover:text-cyan-300 transition-colors flex-shrink-0" />
                </div>
                <p className="text-white/48 text-xs leading-relaxed">{source.desc}</p>
              </a>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-16 bg-[#080714]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <FadeIn>
          <SectionLabel>Next step</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl text-white mb-4 tracking-tight">
            Research becomes stronger when it becomes product architecture.
          </h2>
          <p className="text-white/50 text-sm leading-relaxed max-w-2xl mx-auto mb-7">
            ROMIND KIDS is not built around engagement at any cost. It is built around safe creativity, protected communication, and prevention-first child safety.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/child-safety" data-testid="safety-research-overview-cta" className="px-6 py-3 rounded-full font-semibold text-sm bg-purple-600/80 border border-purple-400/40 text-white hover:bg-purple-500 transition-all text-center">
              Read Safety Overview
            </Link>
            <Link to="/parents" data-testid="safety-research-parents-cta" className="px-6 py-3 rounded-full font-semibold text-sm bg-white/5 border border-white/15 text-white hover:bg-white/10 transition-all text-center flex items-center justify-center gap-2">
              Parent Overview <ArrowRight size={14} />
            </Link>
          </div>
        </FadeIn>
      </div>
    </section>
  </main>
);

export default SafetyResearch;
